import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { inr, mapLink } from "@/lib/format";
import useSeo from "@/hooks/useSeo";
import SearchBar from "@/components/SearchBar";
import HeroSlider from "@/components/HeroSlider";
import { AmenitiesSection, ReviewsSection } from "@/components/HomeExtras";
import { useConfig } from "@/hooks/useConfig";
import RoomCard from "@/components/RoomCard";
import PropertyCard from "@/components/PropertyCard";
import MapEmbed from "@/components/MapEmbed";
import { Reveal } from "@/components/Reveal";

const DIRECT_DEFAULT = [
  "Discount on rooms / packages for 2 and 3 nights",
  "Risk-free booking",
  "Flexible check-in & check-out (subject to availability)",
  "Driver accommodation + meal (subject to availability)",
  "Free room upgrade (subject to availability)",
];

const Hero = ({ properties, cfg, slides }) => (
  <>
  <section className="relative bg-[#1E3B2E] lg:min-h-[100svh] lg:flex lg:flex-col lg:justify-end overflow-hidden" data-testid="home-hero">
    <div className="relative aspect-[4/3] sm:aspect-[16/9] lg:absolute lg:inset-0 lg:aspect-auto"><HeroSlider images={slides} /></div>
    <div className="relative container-x pt-8 pb-10 lg:pt-28 lg:pb-12 lg:pointer-events-none">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.3 }} className="max-w-3xl text-[#FDFBF7] pointer-events-auto">
        <p className="eyebrow text-[#E5D9C5] flex items-center gap-2"><MapPin size={13} /> Kodiyat, Udaipur · Rajasthan</p>
        <h1 className="mt-4 sm:mt-5 text-[2.6rem] sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight font-medium">{cfg.hero_title || "The Ananta Udaipur"}</h1>
        <p className="mt-4 sm:mt-5 text-sm sm:text-lg text-[#FDFBF7]/85 max-w-xl font-light">{cfg.hero_subtitle || "A 90-acre resort set against the Aravalli Hills, by The Ananta Hotels. Cottages, suites and private-pool villas, with breakfast and GST included in every rate."}</p>
        <div className="mt-7 sm:mt-8 flex flex-wrap gap-3">
          <Link to="/book?property=udaipur" className="btn-gold !h-12 sm:!h-14 !px-8 sm:!px-10" data-testid="hero-book-now-btn">Book Now</Link>
          <Link to="/hotels/udaipur" className="btn-ghost !h-12 sm:!h-14 text-white hover:bg-white hover:text-[#1E3B2E]" data-testid="hero-explore-btn">Explore the resort</Link>
        </div>
      </motion.div>
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.6 }} className="mt-14 hidden lg:block pointer-events-auto">
        <SearchBar properties={properties} />
      </motion.div>
      <div className="lg:hidden mt-8"><SearchBar properties={properties} /></div>
    </div>
  </section>
  </>
);

const Featured = ({ udaipur }) => (
  <section className="py-24 sm:py-32">
    <div className="container-x grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
      <Reveal className="lg:col-span-5">
        <p className="eyebrow text-[#C28B5E]">Gateway to serenity</p>
        <h2 className="mt-4 text-4xl sm:text-5xl text-[#1E3B2E] tracking-tight leading-[1.05]">A fairytale escape in the City of Lakes</h2>
        <p className="mt-6 text-[15px] leading-8 text-[#3b3b3b]">{udaipur?.description}</p>
        <ul className="mt-8 grid grid-cols-2 gap-y-3 gap-x-4">
          {udaipur?.amenities.map((a) => <li key={a} className="flex items-center gap-2 text-sm"><Check size={14} className="text-[#C28B5E]" />{a}</li>)}
        </ul>
        {udaipur?.from_price && <p className="mt-8 text-sm text-[#6b6b6b]">Rooms from <span className="font-['Cormorant_Garamond'] text-2xl text-[#1A1A1A]">{inr(udaipur.from_price)}</span> / night, incl. GST &amp; breakfast</p>}
      </Reveal>
      <div className="lg:col-span-7 grid grid-cols-6 grid-rows-6 gap-3 h-[460px] sm:h-[600px]">
        <Reveal className="col-span-4 row-span-4 overflow-hidden"><img src="/images/udaipur/aerial.jpg" alt="Aerial view of The Ananta Udaipur" loading="lazy" className="h-full w-full object-cover" /></Reveal>
        <Reveal delay={0.1} className="col-span-2 row-span-3 overflow-hidden"><img src="/images/udaipur/lobby.jpg" alt="Lobby facade at The Ananta Udaipur" loading="lazy" className="h-full w-full object-cover" /></Reveal>
        <Reveal delay={0.2} className="col-span-2 row-span-3 overflow-hidden"><img src="/images/udaipur/dining.jpg" alt="Outdoor dining at The Ananta Udaipur" loading="lazy" className="h-full w-full object-cover" /></Reveal>
        <Reveal delay={0.15} className="col-span-4 row-span-2 overflow-hidden"><img src="/images/udaipur/float.jpg" alt="Poolside at The Ananta Udaipur" loading="lazy" className="h-full w-full object-cover" /></Reveal>
      </div>
    </div>
  </section>
);

const Rooms = ({ rooms }) => (
  <section className="py-24 bg-[#F4EFE6]" data-testid="home-rooms-section">
    <div className="container-x">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
        <div>
          <p className="eyebrow text-[#C28B5E]">Stay at The Ananta Udaipur</p>
          <h2 className="mt-3 text-4xl sm:text-5xl text-[#1E3B2E] tracking-tight">Cottages, suites &amp; villas</h2>
        </div>
        <Link to="/hotels/udaipur#rooms" className="text-xs uppercase tracking-[0.2em] text-[#1E3B2E] border-b border-[#1E3B2E] pb-1 self-start" data-testid="home-all-rooms-link">All room categories</Link>
      </div>
      <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {rooms.map((r, i) => <Reveal key={r.slug} delay={(i % 4) * 0.08}><RoomCard room={r} propertySlug="udaipur" /></Reveal>)}
      </div>
    </div>
  </section>
);

const DestinationsGrid = ({ properties, title }) => {
  const list = properties.filter((p) => p.slug !== "udaipur");
  return (
    <section className="py-24 sm:py-32" data-testid="home-destinations-section">
      <div className="container-x">
        <p className="eyebrow text-[#C28B5E]">Our destinations</p>
        <div className="mt-3 flex flex-col sm:flex-row sm:items-end justify-between gap-6">
          <h2 className="text-4xl sm:text-5xl text-[#1E3B2E] tracking-tight max-w-2xl" data-testid="home-destinations-title">{title || "Your next escape is waiting"}</h2>
          <Link to="/destinations" className="text-xs uppercase tracking-[0.2em] text-[#1E3B2E] border-b border-[#1E3B2E] pb-1 self-start" data-testid="home-all-destinations-link">View all destinations</Link>
        </div>
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {list.slice(0, 6).map((p, i) => <Reveal key={p.slug} delay={(i % 3) * 0.08}><PropertyCard p={p} tall={i % 3 === 1} /></Reveal>)}
        </div>
      </div>
    </section>
  );
};

const BookDirect = ({ list }) => (
  <section className="bg-[#1E3B2E] text-[#E5D9C5] py-24 relative grain">
    <div className="container-x grid lg:grid-cols-2 gap-12">
      <div>
        <p className="eyebrow text-[#C28B5E]">Why book direct?</p>
        <h2 className="mt-3 text-4xl sm:text-5xl text-[#FDFBF7] tracking-tight">Transparent rates. No surprises.</h2>
        <p className="mt-6 text-sm leading-7 text-[#E5D9C5]/80 max-w-md">Every rate you see includes GST and breakfast. Extra beds are complimentary. The amount on your summary is exactly what you pay.</p>
        <Link to="/book" className="btn-gold mt-8" data-testid="bookdirect-book-btn">Book Now</Link>
      </div>
      <ul className="divide-y divide-[#E5D9C5]/15 border-y border-[#E5D9C5]/15">
        {(list?.length ? list : DIRECT_DEFAULT).map((d) => <li key={d} className="py-5 flex gap-4 items-start text-[15px]"><Check size={18} className="mt-0.5 text-[#C28B5E] shrink-0" />{d}</li>)}
      </ul>
    </div>
  </section>
);

const Experiences = () => (
  <section className="py-24 sm:py-32">
    <div className="container-x grid md:grid-cols-2 gap-6">
      {[["/images/brand/spa.jpg", "Rejuvenate", "International therapies and treatments to leave you relaxed and recharged."], ["/images/brand/wedding.jpg", "Celebrate", "Acres of open spaces and luxurious guestrooms for weddings and events."]].map(([src, t, d]) => (
        <Reveal key={t} className="group">
          <div className="overflow-hidden aspect-[16/10]"><img src={src} alt={t} loading="lazy" className="img-zoom h-full w-full object-cover" /></div>
          <h3 className="mt-6 text-3xl text-[#1E3B2E]">{t} at Ananta</h3>
          <p className="mt-2 text-sm text-[#6b6b6b] max-w-md">{d}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const Location = ({ udaipur }) => (
  <section className="pb-24 sm:pb-32">
    <div className="container-x grid lg:grid-cols-3 gap-10">
      <div>
        <p className="eyebrow text-[#C28B5E]">Find us</p>
        <h2 className="mt-3 text-4xl text-[#1E3B2E] tracking-tight">The Ananta Udaipur</h2>
        <p className="mt-5 text-sm leading-7" data-testid="home-address">{udaipur?.address}</p>
        <a href={mapLink(udaipur?.map_query || "The Ananta Udaipur")} target="_blank" rel="noopener noreferrer" className="btn-ghost mt-6 text-[#1E3B2E] hover:bg-[#1E3B2E] hover:text-white" data-testid="home-directions-btn">Get directions</a>
      </div>
      <div className="lg:col-span-2"><MapEmbed query={udaipur?.map_query || "The Ananta Udaipur"} title="The Ananta Udaipur location" /></div>
    </div>
  </section>
);

export default function Home() {
  useSeo("The Ananta Udaipur - Luxury Resort in Aravalli Hills", "Book The Ananta Udaipur direct: 90-acre luxury resort in Kodiyat, Udaipur with Luxury Cottages, Wooden Cottages, Executive & Grand Suites and Presidential Villa. Rates include GST & breakfast.");
  const cfg = useConfig();
  const [properties, setProperties] = useState([]);
  const [udaipur, setUdaipur] = useState(null);
  useEffect(() => {
    api.get("/properties").then((r) => setProperties(r.data));
    api.get("/properties/udaipur").then((r) => setUdaipur(r.data));
  }, []);
  const u = udaipur && { ...udaipur, from_price: properties.find((p) => p.slug === "udaipur")?.from_price };
  return (
    <>
      <Hero properties={properties} cfg={cfg} slides={udaipur?.images} />
      <Featured udaipur={u} />
      <Rooms rooms={udaipur?.rooms || []} />
      <AmenitiesSection amenities={udaipur?.amenities} />
      <DestinationsGrid properties={properties} title={cfg.home_destinations_title} />
      <BookDirect list={cfg.book_direct} />
      <Experiences />
      <ReviewsSection reviews={cfg.testimonials} mapQuery={udaipur?.map_query} />
      <Location udaipur={udaipur} />
    </>
  );
}
